import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { LocalStorage } from '@freelancer/local-storage';
import { ModalService } from '@freelancer/ui';
import {
  Permissions,
  PermissionStateEnum,
  PermissionType,
} from '@freelancer/ui/permissions';
import { firstValueFrom } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as Rx from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Notifications } from './notifications.service';

/**
 * Manage the push notifications registration, i.e. register/unregister on
 * login/logout
 */
@UntilDestroy()
@Component({
  selector: 'fl-notifications',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private notificationRegistrationSubscription?: Rx.Subscription;

  constructor(
    private auth: Auth,
    private errorHandler: ErrorHandler,
    private localStorage: LocalStorage,
    private modalService: ModalService,
    private notifications: Notifications,
    private permissions: Permissions,

    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.notifications.isSupported()) {
      // On logout, unregister push notifications
      this.auth.registerBeforeLogoutAction(() =>
        this.notifications.unregisterPushNotifications(),
      );

      this.notificationRegistrationSubscription = this.auth
        .isLoggedIn()
        .pipe(
          switchMap(async isLoggedIn => {
            // If logged-in, try registering for push notifications
            if (!isLoggedIn) {
              this.localStorage.set('notificationsRequested', false);
              this.localStorage.set('notificationsEnabled', true);
              // This closes the permissions request modal if the user is
              // logged out. This may be necessary in the case that the user
              // reinstalls the app but has an expired token in the keychain.
              this.modalService.close();
              return;
            }

            // Permission has not been granted yet, do nothing,
            // registerPushNotifications() will be called from the push
            // notifications banner
            const notificationsPermissions =
              await this.permissions.checkPermissions(
                PermissionType.PUSH_NOTIFICATIONS,
              );

            switch (notificationsPermissions) {
              case PermissionStateEnum.GRANTED:
                break;
              case PermissionStateEnum.PROMPT:
              case PermissionStateEnum.PROMPT_WITH_RATIONALE:
                // This is to make sure that we don't try to request permission
                // until the app has finished loading/signing in.
                await firstValueFrom(
                  this.router.events.pipe(
                    filter(
                      event =>
                        event instanceof NavigationEnd &&
                        event.url !== '/internal/pwa' &&
                        event.url !== '/',
                    ),
                    untilDestroyed(this),
                  ),
                );
                break;
              case PermissionStateEnum.DENIED:
              default:
                return;
            }

            // Register push notifications if eligible.
            // In the case we've asked for notifications permissions before
            // but the permission isn't granted or notifications have
            // specifically been disabled by the user, then don't bother
            // attempting to request for it. Otherwise, if it has been granted,
            // continue or we haven't asked for those permissions, then use
            // the usual checks.

            const shouldRegister = await firstValueFrom(
              Rx.combineLatest([
                this.localStorage.get('notificationsRequested'),
                this.localStorage.get('notificationsEnabled'),
              ]).pipe(
                map(([notificationsRequested, notificationsEnabled]) => {
                  const notificationsRequestedButNotGranted =
                    notificationsRequested &&
                    notificationsPermissions !== PermissionStateEnum.GRANTED;
                  return (
                    (!notificationsRequestedButNotGranted &&
                      notificationsEnabled) ??
                    this.notifications.shouldRegisterPushNotifications()
                  );
                }),
                untilDestroyed(this),
              ),
            );

            if (shouldRegister) {
              const registrationResult =
                await this.notifications.registerPushNotifications();
              this.localStorage.set('notificationsRequested', true);
              if (registrationResult.status === 'error') {
                // Log the error as this should not happen as we are checking
                // canShowNotification() first
                const errorMsgString = registrationResult.errorMsg
                  ? `, ${registrationResult.errorMsg}`
                  : '';
                this.errorHandler.handleError(
                  new Error(
                    `registerPushNotifications failed with code ${registrationResult.errorCode}${errorMsgString}`,
                  ),
                );
              }
            }
          }),
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    if (this.notificationRegistrationSubscription) {
      this.notificationRegistrationSubscription.unsubscribe();
    }
  }
}
